import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, Phone, Crown, Camera, Loader2, ArrowRight, Sparkles, ShieldCheck, Zap, Globe } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { fetchMyOnboarding, chooseFreePlan, startProSubscription } from "@/lib/api";
import PhoneOtpModal from "@/components/PhoneOtpModal";
import { FideliteBadge } from "@/components/FideliteBadge";

const PREMIUM_PERKS = [
  { icon: Camera, text: "Jusqu'à 6 photos par annonce (au lieu de 2 en Gratuit)" },
  { icon: Crown, text: "Badge Or « Vendeur Pro » sur toutes vos annonces" },
  { icon: Zap, text: "Remontée automatique toutes les 48 h" },
  { icon: Sparkles, text: "IA Booster illimitée : titres, descriptions, prix" },
  { icon: Globe, text: "Publication dans les 7 pays UEMOA" },
];

export default function ChoisirAbonnementPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/deposer";
  const { user, loading: authLoading, openAuthModal, refresh } = useAuth();
  const [ob, setOb] = useState(null);
  const [busy, setBusy] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const [proAmount, setProAmount] = useState(2000);

  const load = () => fetchMyOnboarding().then(setOb).catch(() => setOb(null));

  useEffect(() => {
    if (authLoading) return;
    if (!user) { openAuthModal(); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const pickFree = async () => {
    setBusy("free");
    try {
      await chooseFreePlan();
      await refresh();
      toast.success("Abonnement Gratuit activé — publiez votre annonce !");
      navigate(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur, réessayez");
    } finally {
      setBusy("");
    }
  };

  const pickPremium = async () => {
    setBusy("premium");
    try {
      const data = await startProSubscription(user.phone_e164 || user.phone || "");
      toast.success("Redirection vers le paiement sécurisé CinetPay…");
      if (data.payment_url.startsWith("http")) window.location.href = data.payment_url;
      else navigate(data.payment_url);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Erreur lors du démarrage du paiement");
      setBusy("");
    }
  };

  if (authLoading || (user && !ob)) {
    return <main className="max-w-5xl mx-auto px-6 py-24 text-center"><Loader2 className="animate-spin mx-auto text-orange-500" /></main>;
  }
  if (!user) {
    return (
      <main className="max-w-5xl mx-auto px-6 py-24 text-center" data-testid="plan-page-anon">
        <h1 className="font-display text-3xl font-extrabold">Connectez-vous pour choisir votre abonnement</h1>
        <button onClick={openAuthModal} className="btn-primary mt-6" data-testid="plan-login-btn">Connexion / Inscription</button>
      </main>
    );
  }

  const phoneOk = ob.steps.phone;
  const planOk = ob.steps.plan;

  return (
    <main className="max-w-5xl mx-auto px-6 py-10" data-testid="plan-page">
      <Stepper steps={ob.steps} />

      {!phoneOk && (
        <section className="glass-card p-6 mt-8 border-2 border-orange-300" data-testid="plan-phone-gate">
          <div className="flex items-start gap-3">
            <Phone className="text-orange-500 shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg font-bold">Étape 1 — Vérifiez votre numéro par SMS</h2>
              <p className="text-sm text-[var(--ink-soft)] mt-1">Un code à 6 chiffres vous est envoyé. Cette vérification protège les acheteurs et les vendeurs contre la fraude.</p>
              <button onClick={() => setOtpOpen(true)} className="btn-primary mt-4" data-testid="plan-verify-phone-btn">Recevoir mon code SMS</button>
            </div>
          </div>
        </section>
      )}

      {phoneOk && planOk && (
        <section className="glass-card p-6 mt-8 border-2 border-emerald-300 bg-emerald-50" data-testid="plan-done">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-600 shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg font-bold text-emerald-800">
                Abonnement {ob.plan === "premium" ? "Premium" : "Gratuit"} actif
                {ob.plan === "premium" && ob.subscription?.current_period_end ? ` — jusqu'au ${new Date(ob.subscription.current_period_end).toLocaleDateString("fr-FR")}` : ""}
              </h2>
              <p className="text-sm text-emerald-800/80 mt-1">{ob.max_photos} photos par annonce · {ob.annonces_count} annonce(s) publiée(s)</p>
              <div className="flex gap-3 mt-4 flex-wrap">
                <Link to="/deposer" className="btn-primary inline-flex items-center gap-2" data-testid="plan-go-deposer">Déposer une annonce <ArrowRight size={16} /></Link>
                {ob.plan !== "premium" && (
                  <button onClick={pickPremium} disabled={busy === "premium"} className="btn-secondary inline-flex items-center gap-2" data-testid="plan-upgrade-btn">
                    <Crown size={16} /> Passer Premium — {proAmount.toLocaleString("fr-FR")} FCFA/mois
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {phoneOk && !planOk && ob.pending_payment && (
        <section className="glass-card p-5 mt-8 border-2 border-amber-300 bg-amber-50" data-testid="plan-pending-payment">
          <p className="text-sm text-amber-900">
            Un paiement Premium est en attente (réf. {ob.pending_payment.transaction_id}). Si vous l'avez réglé, il sera confirmé dans quelques instants.
          </p>
          <div className="flex gap-3 mt-3 flex-wrap">
            {ob.pending_payment.payment_url?.startsWith("http") && (
              <a href={ob.pending_payment.payment_url} className="btn-primary" data-testid="plan-resume-payment">Reprendre le paiement</a>
            )}
            <Link to={`/abonnements/pro/retour?transaction_id=${ob.pending_payment.transaction_id}`} className="btn-secondary" data-testid="plan-check-payment">Vérifier mon paiement</Link>
          </div>
        </section>
      )}

      {phoneOk && !planOk && (
        <section className="mt-8" data-testid="plan-choice">
          <h2 className="text-lg font-bold">Étape 2 — Choisissez votre abonnement</h2>
          <p className="text-sm text-[var(--ink-soft)] mt-1">Vous pourrez passer Premium à tout moment.</p>
          <div className="mt-4"><FideliteBadge onAmount={setProAmount} /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
            <PlanCard
              testid="plan-card-free"
              title="Gratuit"
              price="0 FCFA"
              subtitle="Pour vendre simplement"
              perks={["2 photos par annonce", "Annonces illimitées", "Messagerie et WhatsApp intégrés", "Vérification vendeur par SMS"]}
              cta={busy === "free" ? "Activation…" : "Continuer en Gratuit"}
              onClick={pickFree}
              disabled={!!busy}
            />
            <PlanCard
              testid="plan-card-premium"
              highlight
              title="Premium"
              price={`${proAmount.toLocaleString("fr-FR")} FCFA / mois`}
              subtitle="Pour vendre plus vite"
              perks={PREMIUM_PERKS.map((p) => p.text)}
              cta={busy === "premium" ? "Redirection…" : "Choisir Premium — payer par Mobile Money"}
              onClick={pickPremium}
              disabled={!!busy}
            />
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-4 inline-flex items-center gap-1"><ShieldCheck size={14} /> Paiement sécurisé CinetPay : Orange Money, Wave, MTN MoMo, Moov Money.</p>
        </section>
      )}

      <PhoneOtpModal
        open={otpOpen}
        onOpenChange={setOtpOpen}
        onVerified={async () => { await refresh(); load(); }}
      />
    </main>
  );
}

function Stepper({ steps }) {
  const items = [
    { key: "account", label: "Compte créé" },
    { key: "phone", label: "Téléphone vérifié" },
    { key: "plan", label: "Abonnement choisi" },
    { key: "annonce", label: "Première annonce" },
  ];
  return (
    <ol className="flex items-center gap-2 flex-wrap" data-testid="onboarding-stepper">
      {items.map((it, i) => (
        <li key={it.key} className="flex items-center gap-2" data-testid={`step-${it.key}-${steps[it.key] ? "done" : "todo"}`}>
          {steps[it.key] ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} className="text-[var(--ink-soft)]" />}
          <span className={`text-sm ${steps[it.key] ? "font-semibold" : "text-[var(--ink-soft)]"}`}>{it.label}</span>
          {i < items.length - 1 && <span className="w-6 h-px bg-[var(--line)] mx-1" />}
        </li>
      ))}
    </ol>
  );
}

function PlanCard({ title, price, subtitle, perks, cta, onClick, disabled, highlight, testid }) {
  return (
    <div className={`glass-card p-6 flex flex-col ${highlight ? "border-2 border-orange-400 shadow-lg relative" : ""}`} data-testid={testid}>
      {highlight && <span className="absolute -top-3 left-6 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Recommandé</span>}
      <div className="text-sm text-[var(--ink-soft)]">{subtitle}</div>
      <h3 className="font-display text-2xl font-extrabold mt-1">{title}</h3>
      <div className="text-3xl font-extrabold mt-2 text-orange-600">{price}</div>
      <ul className="mt-4 space-y-2 text-sm flex-1">
        {perks.map((p) => <li key={p} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" /> {p}</li>)}
      </ul>
      <button onClick={onClick} disabled={disabled} className={`${highlight ? "btn-primary" : "btn-secondary"} mt-6 w-full disabled:opacity-50`} data-testid={`${testid}-btn`}>{cta}</button>
    </div>
  );
}
